import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputField from "../../../../components/InputField";
import Button from "../../../../components/Button";
import { Modal } from "react-bootstrap";
import api from "../../../../api/api";
import Select from "react-select";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useAuth } from "../../../../context/AuthContext";
import BackButton from "../../../../components/BackButton";
 
const validationSchema = Yup.object().shape({
  incoterm: Yup.string().required("Incoterm is required"),
  first_shipper_name: Yup.string().required("Shipper Name is required"),
  first_consignee_name: Yup.string().required("Consignee Name is required"),
  pol: Yup.string().required("Port of Loading is required"),
  pod: Yup.string().required("Port of Discharge is required"),
  freight_terms: Yup.string().required("Freight Terms is required"),

  place_of_receipt: Yup.string(),
  place_of_receipt_pin: Yup.string().when("incoterm", ([incoterm], schema) =>
    ["Door to Door", "Door to Port"].includes(incoterm)
      ? schema.required("POR Pin Code is required")
      : schema.notRequired(),
  ),
  final_port_of_delivery: Yup.string(),
  final_port_of_delivery_pin: Yup.string().when("incoterm", ([incoterm], schema) =>
    ["Door to Door", "Port to Door", "ICD to Door"].includes(incoterm)
      ? schema.required("FPOD Pin Code is required")
      : schema.notRequired(),
  ),

  routing: Yup.string().required("Routing is required"),
  transshipment_hub: Yup.string().when("routing", ([routing], schema) =>
    ["Indirect", "Both"].includes(routing)
      ? schema.required("Transshipment Hub is required")
      : schema.notRequired(),
  ),

  shipment_segment: Yup.string().required("Shipment Segment is required"),
  stuffing_destuffing_At: Yup.string().when(
    "shipment_segment",
    ([segment], schema) =>
      ["FE", "LE", "FI", "LI"].includes(segment)
        ? schema.required("Location is required")
        : schema.notRequired(),
  ),
  nature_of_cargo: Yup.string().required("Nature of Cargo is required"),
  haz_class: Yup.string().when("nature_of_cargo", ([cargo], schema) =>
    cargo === "Hazardous"
      ? schema.required("HAZ Class is required")
      : schema.notRequired(),
  ),
  document_type: Yup.string().required("Document Type is required"),
  document_count: Yup.string().when("document_type", ([docType], schema) =>
    docType === "Original"
      ? schema.required("Document Count is required")
      : schema.notRequired(),
  ),
  switch_bl: Yup.string(),
  switch_at: Yup.string().when("switch_bl", ([switchBl], schema) =>
    switchBl === "Yes"
      ? schema.required("Switch At is required")
      : schema.notRequired(),
  ),

  hs_code: Yup.string(),
  hsn_description: Yup.string().required("HSN Description is required"),

  containers: Yup.array().min(1, "At least one container is required"),
  packaging_type: Yup.string().required("Packaging Type is required"),
  packaging_count: Yup.string().required("Packaging Count is required"),
  remarks: Yup.string(),
  goods_value: Yup.string().required("Goods value is required"),
});
 
const EditBookingAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
 
  const [formData, setFormData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [inputRow, setInputRow] = useState({
    containerType: "",
    weight: "",
    length: "",
    breadth: "",
    height: "",
    temperature: "",
  });
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
 
  const containerOptions = [
    { value: "20'Standard", label: "20'Standard" },
    { value: "20'Open Top", label: "20'Open Top" },
    { value: "20'Reefer", label: "20'Reefer" },
    { value: "20'Flat Rack", label: "20'Flat Rack" },
    { value: "20'Platform", label: "20'Platform" },
    { value: "20'Flexi Tank", label: "20'Flexi Tank" },
    { value: "20'ISO Tank", label: "20'ISO Tank" },
    { value: "20'Hard Top", label: "20'Hard Top" },
    { value: "20'Collapsible", label: "20'Collapsible" },
    { value: "40'Standard", label: "40'Standard" },
    { value: "40'High Cube", label: "40'High Cube" },
    { value: "40'Open top", label: "40'Open top" },
    { value: "40'Reefer", label: "40'Reefer" },
    { value: "40'Flat Rack", label: "40'Flat Rack" },
    { value: "40'Platform", label: "40'Platform" },
    { value: "40'Flexi Tank", label: "40'Flexi Tank" },
    { value: "40'ISO Tank", label: "40'ISO Tank" },
    { value: "40'Hard Top", label: "40'Hard Top" },
    { value: "40'Collapsible Flat Rack", label: "40'Collapsible Flat Rack" },
  ];
 
  const incotermOptions = [
    "Port to Port",
    "Door to Door",
    "Door to Port",
    "Port to Door",
    "ICD to Port",
    "ICD to Door",
  ];
 
  const shipmentSegmentOptions = ["FE", "LE", "FI", "LI"];
  const stuffingDestuffingOptions = ["Factory", "Port"];
   const routingOptions = ["Direct", "Indirect", "Both"];
  const natureOfCargoOptions = [
    "Hazardous",
    "Non Hazardous",
    "Temperature Control",
    "Moisture Control",
    "Heavy Duty",
    "Over Dimensional Cargo",
  ];
  const documentTypeOptions = ["Original", "Seaway"];
  const switchBlOptions = [
    { value: "No", label: "No" },
    { value: "Yes", label: "Yes" }
  ];
  const freighttermsOptions = ["Prepaid", "Collect"];
 
  const [portsOptions, setPortsOptions] = useState([]);
 
  // Fetch enquiry details
  const fetchData = async () => {
    try {
      const res = await api.get(`/enquiry/viewEnquiry/${id}`);
      const data = res.data;
      if (!data.switch_bl) {
        data.switch_bl = "No";
      }
      setFormData(data);
      setTableData(
        data.containerDetails?.map((c) => ({
          container_id: c.container_id,
          container_type: c.container_type,
          container_weight: c.container_weight,
          container_length: c.container_length,
          container_breadth: c.container_breadth,
          container_height: c.container_height,
          container_temperature: c.container_temperature,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching enquiry:", error);
    } finally {
      setLoading(false);
    }
  };
 
  const fetchPorts = async () => {
    try {
      const res = await api.get("/ports");
      const options = res.data.map((p) => ({
        value: p.Port_names,
        label: p.Port_names,
      }));
      setPortsOptions(options);
    } catch (err) {
      console.error("Error fetching ports:", err);
    }
  };
 
  useEffect(() => {
    fetchData();
    fetchPorts();
  }, []);
 
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formData || {
      incoterm: "",
      first_shipper_name: "",
      first_consignee_name: "",
      pol: "",
      pod: "",
      freight_terms: "",
      place_of_receipt: "",
      place_of_receipt_pin: "",
      final_port_of_delivery: "",
      final_port_of_delivery_pin: "",
      routing: "",
      transshipment_hub: "",
      shipment_segment: "",
      stuffing_destuffing_At: "",
      nature_of_cargo: "",
      haz_class: "",
      document_type: "",
      document_count: "",
      switch_bl: "No",
      switch_at: "",
      hs_code: "",
      hs_description: "",
      packaging_type: "",
      packaging_count: "",
      goods_value: "",
      remarks: "",
      cha_required: "",
      containers: [],
    },
    validationSchema,
    onSubmit: (values) => {
      console.log("Formik onSubmit triggered", {
        values,
        tableDataLength: tableData.length,
        tableData,
        errors: formik.errors,
        isValid: formik.isValid,
        submitCount: formik.submitCount,
      });
      if (tableData.length === 0) {
        alert("At least one container is required");
        return;
      }
      setShowConfirm(true);
    },
  });
 
  const handleAddRow = () => {
    if (
      !inputRow.containerType &&
      formik.values.shipment_segment !== "LE" &&
      formik.values.shipment_segment !== "LI"
    ) {
      alert("Please select a container type");
      return;
    }
 
    // Default empty numeric fields to 0
    const newContainer = {
      container_type: inputRow.containerType,
      container_weight: inputRow.weight || 0,
      container_length: inputRow.length || 0,
      container_breadth: inputRow.breadth || 0,
      container_height: inputRow.height || 0,
      container_temperature: inputRow.temperature || 0,
    };
 
    setTableData((prev) => [...prev, newContainer]);
    setInputRow({
      containerType: "",
      weight: "",
      length: "",
      breadth: "",
      height: "",
      temperature: "",
    });
  };
 
  const handleRemoveRow = (index) => {
    setTableData((prev) => prev.filter((_, i) => i !== index));
  };
 
  const handleConfirm = async () => {
    console.log("handleConfirm triggered", {
      url: `/enquiry/updateEnquiry/${id}`,
      payload: { ...formik.values, containers: tableData },
    });
    try {
      const res = await api.put(`/enquiry/updateEnquiry/${id}`, {
        ...formik.values,
        containers: tableData,
      });
      console.log("API update success:", res.data);
      setShowConfirm(false);
      setShowSuccess(true);
    } catch (err) {
      console.error("Error updating enquiry:", err);
      setShowConfirm(false);
    }
  };
 
  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate("/freight/view-booking/admin");
  };
 
 
  const getFieldError = (fieldName) => {
    return formik.touched[fieldName] && formik.errors[fieldName]
      ? formik.errors[fieldName]
      : "";
  };
 
  const hasFieldError = (fieldName) => {
    return formik.touched[fieldName] && formik.errors[fieldName];
 
  };
      const isLE_LI = ["LE", "LI"].includes(formik.values.shipment_segment);
  const isFE_FI = ["FE", "FI"].includes(formik.values.shipment_segment);
 
  if (loading || !formData)
    return <p className="text-center mt-4">Loading Booking Details...</p>;
 
  return (
    <>
      <BackButton />
      <form
        onSubmit={(e) => {
          console.log("form onSubmit event fired");
          e.preventDefault();
          console.log("formik state before handleSubmit:", {
            isValid: formik.isValid,
            errors: formik.errors,
            valuesKeys: Object.keys(formik.values),
            submitCount: formik.submitCount,
          });
          try {
            formik.handleSubmit(e);
          } catch (err) {
            console.error("handleSubmit error:", err);
          }
          console.log("formik state right after handleSubmit:", {
            errors: formik.errors,
            dirty: formik.dirty,
            isValid: formik.isValid,
          });
        }}
      >
        <div className="row g-3">
          {/* Incoterm */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Incoterm</label>
            <Select
              placeholder="Select Incoterm"
              options={incotermOptions.map((i) => ({ value: i, label: i }))}
              value={
                formik.values.incoterm
                  ? {
                      value: formik.values.incoterm,
                      label: formik.values.incoterm,
                    }
                  : null
              }
              onChange={(opt) =>
                formik.setFieldValue("incoterm", opt?.value || "")
              }
              onBlur={() => formik.setFieldTouched("incoterm", true)}
              isClearable
            />
            {hasFieldError("incoterm") && (
              <div className="text-danger small mt-1">
                {getFieldError("incoterm")}
              </div>
            )}
          </div>

          <div className="col-sm-6 col-md-4">
            <label className="form-label">Shipper Name</label>
            <InputField
              name="first_shipper_name"
              value={formik.values.first_shipper_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {hasFieldError("first_shipper_name") && (
              <div className="text-danger small mt-1">
                {getFieldError("first_shipper_name")}
              </div>
            )}
          </div>

          {/* Consignee Name */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Consignee Name</label>
            <InputField
              name="first_consignee_name"
              value={formik.values.first_consignee_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {hasFieldError("first_consignee_name") && (
              <div className="text-danger small mt-1">
                {getFieldError("first_consignee_name")}
              </div>
            )}
          </div>
            <div className="col-sm-6 col-md-4">
            <label className="form-label">Place Of Receipt</label>
            <InputField
              name="place_of_receipt"
              value={formik.values.place_of_receipt}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {hasFieldError("place_of_receipt") && (
              <div className="text-danger small mt-1">
                {getFieldError("place_of_receipt")}
              </div>
            )}
          </div>

          {/*  POR Pin Code */}
          {["Door to Door", "Door to Port"].includes(
            formik.values.incoterm,
          ) && (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">POR Pin Code</label>
              <InputField
                name="place_of_receipt_pin"
                value={formik.values.place_of_receipt_pin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {hasFieldError("place_of_receipt_pin") && (
                <div className="text-danger small mt-1">
                  {getFieldError("place_of_receipt_pin")}
                </div>
              )}
            </div>
          )}
 
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Port of Loading</label>
            <Select
              options={portsOptions}
              value={
                formik.values.pol
                  ? portsOptions.find((p) => p.value === formik.values.pol)
                  : null
              }
              onChange={(opt) => formik.setFieldValue("pol", opt?.value || "")}
              onBlur={() => formik.setFieldTouched("pol", true)}
              isClearable
            />
            {hasFieldError("pol") && (
              <div className="text-danger small mt-1">
                {getFieldError("pol")}
              </div>
            )}
          </div>
 
          {/* Port of Discharge */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Port of Discharge</label>
            <Select
              options={portsOptions}
              value={
                formik.values.pod
                  ? portsOptions.find((p) => p.value === formik.values.pod)
                  : null
              }
              onChange={(opt) => formik.setFieldValue("pod", opt?.value || "")}
              onBlur={() => formik.setFieldTouched("pod", true)}
              isClearable
            />
            {hasFieldError("pod") && (
              <div className="text-danger small mt-1">
                {getFieldError("pod")}
              </div>
            )}
          </div>


          {/*  Final Port Of Delivery (FPOD) */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Final Port Of Delivery</label>
            <InputField
              name="final_port_of_delivery"
              value={formik.values.final_port_of_delivery}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {hasFieldError("final_port_of_delivery") && (
              <div className="text-danger small mt-1">
                {getFieldError("final_port_of_delivery")}
              </div>
            )}
          </div>

          {/*  FPOD Pin Code */}
          {["Door to Door", "Port to Door", "ICD to Door"].includes(
            formik.values.incoterm,
          ) && (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">FPOD Pin Code</label>
              <InputField
                name="final_port_of_delivery_pin"
                value={formik.values.final_port_of_delivery_pin}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {hasFieldError("final_port_of_delivery_pin") && (
                <div className="text-danger small mt-1">
                  {getFieldError("final_port_of_delivery_pin")}
                </div>
              )}
            </div>
          )}

           {/*  Routing */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Routing</label>
            <Select
              options={routingOptions.map((r) => ({ value: r, label: r }))}
              placeholder="Select Routing"
              value={
                formik.values.routing
                  ? {
                      value: formik.values.routing,
                      label: formik.values.routing,
                    }
                  : null
              }
              onChange={(opt) =>
                formik.setFieldValue("routing", opt?.value || "")
              }
              onBlur={() => formik.setFieldTouched("routing", true)}
              isClearable
            />
            {hasFieldError("routing") && (
              <div className="text-danger small mt-1">
                {getFieldError("routing")}
              </div>
            )}
          </div>

          {/*  Transshipment Hub */}
          {["Indirect", "Both"].includes(formik.values.routing) && (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">Transshipment Hub</label>
              <Select
                options={portsOptions}
                value={
                  portsOptions.find(
                    (p) => p.value === formik.values.transshipment_hub,
                  ) || null
                }
                onChange={(opt) =>
                  formik.setFieldValue("transshipment_hub", opt?.value || "")
                }
                onBlur={() => formik.setFieldTouched("transshipment_hub", true)}
                isClearable
              />
              {hasFieldError("transshipment_hub") && (
                <div className="text-danger small mt-1">
                  {getFieldError("transshipment_hub")}
                </div>
              )}
            </div>
          )}
 
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Freight Terms</label>
            <Select
              placeholder="Select Freight Terms"
              options={freighttermsOptions.map((i) => ({ value: i, label: i }))}
              value={
                formik.values.freight_terms
                  ? {
                      value: formik.values.freight_terms,
                      label: formik.values.freight_terms,
                    }
                  : null
              }
              onChange={(opt) =>
                formik.setFieldValue("freight_terms", opt?.value || "")
              }
              onBlur={() => formik.setFieldTouched("freight_terms", true)}
              isClearable
            />
            {hasFieldError("freight_terms") && (
              <div className="text-danger small mt-1">
                {getFieldError("freight_terms")}
              </div>
            )}
          </div>

          <div className="col-sm-6 col-md-4">
            <label className="form-label">Shipment Segment</label>
            <Select
              placeholder="Select Segment"
              options={shipmentSegmentOptions.map((i) => ({
                value: i,
                label: i,
              }))}
              value={
                formik.values.shipment_segment
                  ? {
                      value: formik.values.shipment_segment,
                      label: formik.values.shipment_segment,
                    }
                  : null
              }
              onChange={(opt) =>
                formik.setFieldValue("shipment_segment", opt?.value || "")
              }
              onBlur={() => formik.setFieldTouched("shipment_segment", true)}
              isClearable
            />
            {hasFieldError("shipment_segment") && (
              <div className="text-danger small mt-1">
                {getFieldError("shipment_segment")}
              </div>
            )}
          </div>
 
          {["FE", "LE"].includes(formik.values.shipment_segment) && (
           <div className="col-sm-6 col-md-4">
              <label className="form-label">Stuffing At</label>
              <Select
                options={stuffingDestuffingOptions.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
                placeholder="Select Stuffing Location"
                value={
                  formik.values.stuffing_destuffing_At
                    ? {
                        value: formik.values.stuffing_destuffing_At,
                        label: formik.values.stuffing_destuffing_At,
                      }
                    : null
                }
                onChange={(opt) =>
                  formik.setFieldValue(
                    "stuffing_destuffing_At",
                    opt?.value || "",
                  )
                }
                onBlur={() =>
                  formik.setFieldTouched("stuffing_destuffing_At", true)
                }
                isClearable
              />
              {hasFieldError("stuffing_destuffing_At") && (
                <div className="text-danger small mt-1">
                  {getFieldError("stuffing_destuffing_At")}
                </div>
              )}
            </div>
          )}
          {["FI", "LI"].includes(formik.values.shipment_segment) && (
             <div className="col-sm-6 col-md-4">
              <label className="form-label">Destuffing At</label>
              <Select
                options={stuffingDestuffingOptions.map((opt) => ({
                  value: opt,
                  label: opt,
                }))}
                placeholder="Select Destuffing Location"
                value={
                  formik.values.stuffing_destuffing_At
                    ? {
                        value: formik.values.stuffing_destuffing_At,
                        label: formik.values.stuffing_destuffing_At,
                      }
                    : null
                }
                onChange={(opt) =>
                  formik.setFieldValue(
                    "stuffing_destuffing_At",
                    opt?.value || "",
                  )
                }
                onBlur={() =>
                  formik.setFieldTouched("stuffing_destuffing_At", true)
                }
                isClearable
              />
              {hasFieldError("stuffing_destuffing_At") && (
                <div className="text-danger small mt-1">
                  {getFieldError("stuffing_destuffing_At")}
                </div>
              )}
            </div>
          )}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Nature of Cargo</label>
            <Select
              placeholder="Select Cargo Type"
              options={natureOfCargoOptions.map((i) => ({
                value: i,
                label: i,
              }))}
              value={
                formik.values.nature_of_cargo
                  ? {
                      value: formik.values.nature_of_cargo,
                      label: formik.values.nature_of_cargo,
                    }
                  : null
              }
              onChange={(opt) =>
                formik.setFieldValue("nature_of_cargo", opt?.value || "")
              }
              onBlur={() => formik.setFieldTouched("nature_of_cargo", true)}
              isClearable
            />
            {hasFieldError("nature_of_cargo") && (
              <div className="text-danger small mt-1">
                {getFieldError("nature_of_cargo")}
              </div>
            )}
          </div>
 
          {formik.values.nature_of_cargo === "Hazardous" && (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">HAZ Class</label>
              <InputField
                value={formik.values.haz_class}
                onChange={formik.handleChange("haz_class")}
                onBlur={formik.handleBlur("haz_class")}
              />
              {hasFieldError("haz_class") && (
                <div className="text-danger small mt-1">
                  {getFieldError("haz_class")}
                </div>
              )}
            </div>
          )}
 
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Document Type</label>
            <Select
              placeholder="Select Document Type"
              options={documentTypeOptions.map((i) => ({ value: i, label: i }))}
              value={
                formik.values.document_type
                  ? {
                      value: formik.values.document_type,
                      label: formik.values.document_type,
                    }
                  : null
              }
              onChange={(opt) =>
                formik.setFieldValue("document_type", opt?.value || "")
              }
              onBlur={() => formik.setFieldTouched("document_type", true)}
              isClearable
            />
            {hasFieldError("document_type") && (
              <div className="text-danger small mt-1">
                {getFieldError("document_type")}
              </div>
            )}
          </div>
 
          {formik.values.document_type === "Original" && (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">Document Count</label>
              <InputField
                value={formik.values.document_count}
                onChange={formik.handleChange("document_count")}
                onBlur={formik.handleBlur("document_count")}
              />
              {hasFieldError("document_count") && (
                <div className="text-danger small mt-1">
                  {getFieldError("document_count")}
                </div>
              )}
            </div>
          )}
 
          {/* Switch BL Dropdown */}
          <div className="col-sm-6 col-md-4">
            <label className="form-label">Switch BL</label>
            <Select
              options={switchBlOptions}
              value={switchBlOptions.find(opt => opt.value === formik.values.switch_bl) || switchBlOptions[0]}
              onChange={(val) => formik.setFieldValue("switch_bl", val ? val.value : "")}
              onBlur={() => formik.setFieldTouched("switch_bl", true)}
              isClearable
            />
            {hasFieldError("switch_bl") && (
              <div className="text-danger small mt-1">
                {getFieldError("switch_bl")}
              </div>
            )}
          </div>
          {formik.values.switch_bl === "Yes" && (
            <div className="col-sm-6 col-md-4">
              <label className="form-label">Switch At</label>
              <Select
                options={portsOptions}
                value={portsOptions.find(opt => opt.value === formik.values.switch_at) || null}
                onChange={(val) => formik.setFieldValue("switch_at", val ? val.value : "")}
                isSearchable
                placeholder="Select Port..."
              />
              {hasFieldError("switch_at") && (
                <div className="text-danger small mt-1">
                  {getFieldError("switch_at")}
                </div>
              )}
            </div>
          )}
 
          <div className="col-sm-6 col-md-4">
            <label className="form-label">HS Code</label>
            <InputField
              value={formik.values.hs_code}
              onChange={formik.handleChange("hs_code")}
              onBlur={formik.handleBlur("hs_code")}
            />
            {hasFieldError("hs_code") && (
              <div className="text-danger small mt-1">
                {getFieldError("hs_code")}
              </div>
            )}
          </div>
          <div className="col-sm-6 col-md-4">
            <label className="form-label">HSN Description</label>
            <InputField
              value={formik.values.hs_description}
              onChange={formik.handleChange("hs_description")}
              onBlur={formik.handleBlur("hs_description")}
            />
            {hasFieldError("hs_description") && (
              <div className="text-danger small mt-1">
                {getFieldError("hs_description")}
              </div>
            )}
          </div>
           <div className="col-12 col-sm-6 col-md-4">
            <label className="form-label">Packaging Type</label>
            <InputField
              value={formik.values.packaging_type}
              onChange={formik.handleChange("packaging_type")}
              onBlur={formik.handleBlur("packaging_type")}
            />
            {hasFieldError("packaging_type") && (
              <div className="text-danger small mt-1">
                placeholder="Enter packaging Type"
                {getFieldError("packaging_type")}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-md-4">
            <label className="form-label">Packaging Count</label>
            <InputField
              value={formik.values.packaging_count}
              onChange={formik.handleChange("packaging_count")}
              onBlur={formik.handleBlur("packaging_count")}
            />
            {hasFieldError("packaging_count") && (
              <div className="text-danger small mt-1">
                placeholder="Enter packaging Count"
                {getFieldError("packaging_count")}
              </div>
            )}
          </div>
           <div className="col-12 col-sm-6 col-md-4">
               <label className="form-label">Goods Value</label>
              <InputField
                type="number"
                value={formik.values.goods_value}
                placeholder="Enter Goods value"
              onChange={formik.handleChange("goods_value")}
              onBlur={formik.handleBlur("goods_value")}
              />
            </div>
 
          <div className="position-relative">
            <div className="position-absolute measurement d-none d-lg-block">
              <p>1M = 100CM</p>
              <p>1CM = 10MM</p>
            </div>
 
            <table className="table table-bordered tbllbl mb-2 mb-md-3">
              <thead className="table-light">
                <tr>
                   {["FE", "FI"].includes(formData.shipment_segment) && (
                      <th>Container Type</th>
                    )}
                 
                  {["FE", "FI"].includes(formData.shipment_segment) && (
                      <th>Gross Cargo Weight</th>
                    )}
                   {["LE", "LI"].includes(formData.shipment_segment) && (
                   <th>Length</th>
                    )}
                     {["LE", "LI"].includes(formData.shipment_segment) && (
                   <th>Breadth</th>
                     )}
                      {["LE", "LI"].includes(formData.shipment_segment) && (
                  <th>Height</th>
                      )}
                  {formik.values.nature_of_cargo === "Temperature Control" && (
                    <th>Temperature</th>
                  )}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                {["FE", "FI"].includes(formData.shipment_segment) && (
                      <td>
                        <Select
                          value={
                            inputRow.containerType
                              ? {
                                  value: inputRow.containerType,
                                  label: inputRow.containerType,
                                }
                              : null
                          }
                          onChange={(opt) =>
                            setInputRow((prev) => ({
                              ...prev,
                              containerType: opt?.value || "",
                            }))
                          }
                          options={containerOptions}
                          placeholder="Select Container"
                          isClearable
                        />
                      </td>
                    )}
                   {["FE", "FI"].includes(formData.shipment_segment) && (
                  <td>
                    <InputField
                      value={inputRow.weight}
                      type="number"
                      placeholder="Weight in kg's"
                      onChange={(e) =>
                        setInputRow((prev) => ({
                          ...prev,
                          weight: e.target.value,
                        }))
                      }
                    />
                  </td>
                    )}
                 
                   {["LE", "LI"].includes(formData.shipment_segment) && (
                    <>
                  <td>
                    <InputField
                      value={inputRow.length}
                      type="number"
                      placeholder="Length in cm's"
                      onChange={(e) =>
                        setInputRow((prev) => ({
                          ...prev,
                          length: e.target.value,
                        }))
                      }
                    />
                  </td>
                 
                 
                 
                  <td>
                    <InputField
                      value={inputRow.breadth}
                      type="number"
                      placeholder="Breadth in cm's"
                      onChange={(e) =>
                        setInputRow((prev) => ({
                          ...prev,
                          breadth: e.target.value,
                        }))
                      }
                    />
                  </td>
                   
                 
                  <td>
                    <InputField
                      value={inputRow.height}
                      type="number"
                      placeholder="Height in cm's"
                      onChange={(e) =>
                        setInputRow((prev) => ({
                          ...prev,
                          height: e.target.value,
                        }))
                      }
                    />
                  </td>
                  </>
                    )}
                   
                  {formik.values.nature_of_cargo === "Temperature Control" && (
                    <td>
                      <InputField
                        value={inputRow.temperature}
                        type="number"
                         placeholder="Tempreature in 'C"
                        onChange={(e) =>
                          setInputRow((prev) => ({
                            ...prev,
                            temperature: e.target.value,
                          }))
                        }
                      />
                    </td>
                  )}
                  <td className="text-center">
                    <AddCircleOutlineIcon
                      style={{
                        cursor: "pointer",
                        color: "green",
                        fontSize: 28,
                      }}
                      onClick={handleAddRow}
                    />
                  </td>
                </tr>
                {tableData.map((container, index) => (
                  <tr key={index}>
                  {["FE", "FI"].includes(formData.shipment_segment) && (
                        <td>{container.container_type}</td>
                      )}
                     {["FE", "FI"].includes(formData.shipment_segment) && (
                    <td>{container.container_weight}</td>
                      )}
                    {["LE", "LI"].includes(formData.shipment_segment) && (
                    <td>{container.container_length}</td>
                      )}
                       {["LE", "LI"].includes(formData.shipment_segment) && (
                     <td>{container.container_breadth}</td>
                       )}
                      {["LE", "LI"].includes(formData.shipment_segment) && (
                     <td>{container.container_height}</td>
                        )}
                    {formik.values.nature_of_cargo ===
                      "Temperature Control" && (
                      <td>{container.container_temperature}</td>
                    )}
                    <td className="text-center">
                      <DeleteOutlineIcon
                        style={{
                          cursor: "pointer",
                          color: "red",
                          fontSize: 28,
                        }}
                        onClick={() => handleRemoveRow(index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {tableData.length === 0 && (
              <div className="text-danger small mt-1">
                At least one container is required
              </div>
            )}
          </div>
        </div>
        <div className="row g-3">
         
           
            <div className="col-sm-6 col-md-4">
              <label className="form-label">CHA Services Required?</label>
              <Select
                options={[
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ]}
                value={[
                  { label: "Yes", value: "yes" },
                  { label: "No", value: "no" },
                ].find((option) => option.value === formik.values.cha_required.toLocaleLowerCase())}
                onChange={(option) =>
                  formik.setFieldValue("cha_required", option?.value || "")
                }
                onBlur={() => formik.setFieldTouched("cha_required", true)}
                isClearable
              />
            </div>
          <div className="col-12 col-sm-6 col-md-4">
            <label className="form-label">Remarks</label>
            <InputField
              value={formik.values.remarks}
              placeholder="Enter Remarks"
              onChange={formik.handleChange("remarks")}
              onBlur={formik.handleBlur("remarks")}
            />
          </div>
        </div>
 
        <div className="text-end mt-3">
          <Button
            text="Update Booking"
            variant="primary"
            type="submit"
            // onClick={() => console.log("Button onClick fired")}
             onClick={() => {
              setShowConfirm(true);
            }}
          />
        </div>
      </form>
 
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to update this booking?</Modal.Body>
        <Modal.Footer>
          <Button
            text="Cancel"
            variant="secondary"
            onClick={() => setShowConfirm(false)}
          />
          <Button text="Confirm" variant="primary" onClick={handleConfirm} />
        </Modal.Footer>
      </Modal>
 
      <Modal show={showSuccess} onHide={handleSuccessClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Booking updated successfully!</Modal.Body>
        <Modal.Footer>
          <Button text="OK" variant="success" onClick={handleSuccessClose} />
        </Modal.Footer>
      </Modal>
    </>
  );
};
 
export default EditBookingAdmin;